import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  ShieldCheckIcon,
  ChartBarIcon,
  CreditCardIcon,
  AdjustmentsHorizontalIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { cookieManager, useCookiePreferences } from "utils/cookieManager";

interface CookieSettingsProps {
  onClose?: () => void;
  standalone?: boolean;
}

const CookieSettings: React.FC<CookieSettingsProps> = ({ onClose, standalone = false }) => {
  const { preferences, hasConsent } = useCookiePreferences();
  const [currentPreferences, setCurrentPreferences] = useState(
    preferences || {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    }
  );
  const [showDetails, setShowDetails] = useState<string | null>(null);

  useEffect(() => {
    if (preferences) {
      setCurrentPreferences(preferences);
    }
  }, [preferences]);

  const handleSavePreferences = () => {
    // Sauvegarder via cookieManager
    localStorage.setItem("eventToBee_cookiePreferences", JSON.stringify(currentPreferences));
    localStorage.setItem(
      "eventToBee_cookieConsent",
      JSON.stringify({
        timestamp: new Date().toISOString(),
        version: "1.0",
      })
    );

    cookieManager.refreshPreferences();

    if (onClose) {
      onClose();
    }
  };

  const togglePreference = (key: keyof typeof currentPreferences) => {
    if (key === "necessary") return;

    setCurrentPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const cookieTypes = [
    {
      key: "necessary" as const,
      name: "Cookies nécessaires",
      description: "Requis pour le fonctionnement de base du site",
      details:
        "Ces cookies sont indispensables pour que le site fonctionne correctement. Ils incluent l'authentification, la sécurité, et les préférences essentielles.",
      icon: ShieldCheckIcon,
      examples: [
        "Token d'authentification sécurisé",
        "Protection CSRF",
        "Gestion des sessions",
        "Préférences de sécurité",
      ],
      required: true,
      color: "green",
    },
    {
      key: "functional" as const,
      name: "Cookies fonctionnels",
      description: "Améliorent votre expérience utilisateur",
      details:
        "Ces cookies mémorisent vos préférences comme le thème, la langue, et les paramètres d'affichage pour une expérience personnalisée.",
      icon: AdjustmentsHorizontalIcon,
      examples: [
        "Préférences de thème (sombre/clair)",
        "Paramètres d'affichage",
        "Préférences linguistiques",
        "Sauvegarde des filtres",
      ],
      required: false,
      color: "blue",
    },
    {
      key: "analytics" as const,
      name: "Cookies d'analyse",
      description: "Nous aident à améliorer le site",
      details:
        "Ces cookies collectent des données anonymes sur l'utilisation du site pour nous aider à comprendre les besoins des utilisateurs et améliorer nos services.",
      icon: ChartBarIcon,
      examples: [
        "Google Analytics (données anonymisées)",
        "Statistiques de pages visitées",
        "Mesure de performance",
        "Analyse du parcours utilisateur",
      ],
      required: false,
      color: "purple",
    },
    {
      key: "marketing" as const,
      name: "Cookies marketing",
      description: "Pour des publicités pertinentes",
      details:
        "Ces cookies permettent d'afficher des publicités pertinentes et de mesurer l'efficacité des campagnes publicitaires.",
      icon: CreditCardIcon,
      examples: [
        "Suivi des conversions Stripe",
        "Personnalisation publicitaire",
        "Mesure d'efficacité des campagnes",
        "Cookies de réseaux sociaux",
      ],
      required: false,
      color: "orange",
    },
  ];

  const getColorClasses = (color: string, active: boolean) => {
    const colors = {
      green: active ? "bg-green-100 border-green-500 text-green-800" : "bg-gray-50 border-gray-200 text-gray-600",
      blue: active ? "bg-blue-100 border-blue-500 text-blue-800" : "bg-gray-50 border-gray-200 text-gray-600",
      purple: active
        ? "bg-purple-100 border-purple-500 text-purple-800"
        : "bg-gray-50 border-gray-200 text-gray-600",
      orange: active
        ? "bg-orange-100 border-orange-500 text-orange-800"
        : "bg-gray-50 border-gray-200 text-gray-600",
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  const getIconColorClasses = (color: string) => {
    const colors = {
      green: "text-green-600",
      blue: "text-blue-600",
      purple: "text-purple-600",
      orange: "text-orange-600",
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  const containerClasses = standalone
    ? "max-w-4xl mx-auto p-6 bg-white dark:bg-gray-900 rounded-xl shadow-lg"
    : "w-full";

  return (
    <div className={containerClasses}>
      {standalone && (
        <div className="mb-8 text-center">
          <div className="flex justify-center items-center gap-3 mb-4">
            <span className="text-4xl">🍪</span>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Paramètres des cookies</h1>
          </div>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            Gérez vos préférences de cookies en toute transparence
          </p>
          <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              🔒 Vos données sont protégées conformément au RGPD. Vous pouvez modifier ces paramètres à tout
              moment.
            </p>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {cookieTypes.map((type) => {
          const Icon = type.icon;
          const isActive = currentPreferences[type.key];
          const isExpanded = showDetails === type.key;

          return (
            <motion.div
              key={type.key}
              layout
              className={`border-2 rounded-xl p-6 transition-all duration-200 ${getColorClasses(type.color, isActive || type.required)}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4 flex-1">
                  <Icon className={`w-6 h-6 mt-1 ${getIconColorClasses(type.color)}`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{type.name}</h3>
                      {type.required && (
                        <span className="px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 text-xs font-medium rounded-full">
                          Requis
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{type.description}</p>

                    <button
                      onClick={() => setShowDetails(isExpanded ? null : type.key)}
                      className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                    >
                      <InformationCircleIcon className="w-4 h-4" />
                      {isExpanded ? "Masquer les détails" : "Voir les détails"}
                    </button>
                  </div>
                </div>

                <div className="flex items-center ml-4">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={() => togglePreference(type.key)}
                      disabled={type.required}
                      className="sr-only peer"
                    />
                    <div
                      className={`relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 ${isActive || type.required ? "peer-checked:bg-blue-600" : ""} ${type.required ? "opacity-50 cursor-not-allowed" : ""}`}
                    ></div>
                  </label>
                </div>
              </div>

              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600"
                >
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">{type.details}</p>

                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      Exemples de cookies utilisés :
                    </h4>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                      {type.examples.map((example, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-gray-400 mt-2">•</span>
                          {example}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </div>

      <div className="mt-8 p-6 bg-gray-50 dark:bg-gray-800 rounded-xl">
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-1">Résumé de vos choix</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {Object.values(currentPreferences).filter(Boolean).length} sur{" "}
              {Object.keys(currentPreferences).length} types de cookies autorisés
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            {onClose && (
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Annuler
              </button>
            )}
            <button
              onClick={handleSavePreferences}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <span>🍪</span>
              Enregistrer les préférences
            </button>
          </div>
        </div>
      </div>

      {standalone && (
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Dernière mise à jour : {hasConsent ? new Date().toLocaleDateString("fr-FR") : "Non défini"}
          </p>
          <div className="mt-4 flex justify-center gap-6 text-sm">
            <a href="/privacy" className="text-blue-600 hover:underline">
              Politique de confidentialité
            </a>
            <a href="/cookies" className="text-blue-600 hover:underline">
              Politique des cookies
            </a>
            <a href="/contact" className="text-blue-600 hover:underline">
              Nous contacter
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default CookieSettings;
